import React, { useState } from 'react';
import Modal from '../Modal';
import { Camion } from '../../types';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface DeleteVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  vehicle: Camion;
}

const DeleteVehicleModal: React.FC<DeleteVehicleModalProps> = ({ isOpen, onClose, onConfirm, vehicle }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsDeleting(true);
    setError(null);
    
    try {
      await onConfirm();
      // Le modal sera fermé par le composant parent après succès
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      setError(error.message || 'Une erreur est survenue lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setError(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Confirmer la suppression" size="md">
      <div className="space-y-6">
        {/* Icône d'avertissement */}
        <div className="flex justify-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
        </div>

        {/* Message de confirmation */}
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Supprimer ce véhicule ?
          </h3>
          <p className="text-sm text-gray-500">
            Êtes-vous sûr de vouloir supprimer le véhicule{' '}
            <span className="font-medium text-gray-900">
              {vehicle.immatriculation}
            </span>{' '}
            ({vehicle.marque} {vehicle.modele}) ?
          </p>
          <p className="text-sm text-red-600 mt-2">
            Cette action est irréversible et supprimera définitivement le véhicule.
          </p>
        </div>

        {/* Informations du véhicule */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Immatriculation:</span>
              <p className="text-gray-900">{vehicle.immatriculation}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Marque:</span>
              <p className="text-gray-900">{vehicle.marque}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Modèle:</span>
              <p className="text-gray-900">{vehicle.modele}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Propriétaire:</span>
              <p className="text-gray-900">{vehicle.proprietaire}</p>
            </div>
          </div>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isDeleting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Suppression...
              </>
            ) : (
              'Supprimer'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteVehicleModal;
